import { useCallback, useEffect, useRef, useState } from 'react';
import {
  createQrSignInCode,
  readQrSignInStatus,
  QrSignInCode,
  QrSignInPollArgs,
  QrSignInStatus
} from 'services/api/auth';

const POLL_INTERVAL_MS = 2000;
// Replace a code comfortably inside its life, so a dead QR is never left on screen. The backend
// currently hands out 180-second codes; `expires_in` is what actually decides.
const REPLACE_CODE_BEFORE_EXPIRY_MS = 10000;
const FALLBACK_LIFETIME_MS = 180000;

type QrSignInPhase = 'loading' | 'showing' | 'error';

type ReadyStatus = Extract<QrSignInStatus, { status: 'ready' }>;

type QrSignInResult = {
  phase: QrSignInPhase;
  /** The exact string the backend asked us to render. Never rebuilt from parts. */
  url: string | null;
  retry: () => void;
};

type Params = {
  enabled: boolean;
  onReady: (payload: { user: ReadyStatus['user']; token: ReadyStatus['token'] }) => void;
};

/**
 * Drives the whole Web Sign-In Code handshake: ask for a code, render one value, keep the other
 * one private, poll until the phone redeems it, and replace the code before it expires.
 *
 * `poll_secret` lives only in a local variable inside the running loop — never in React state,
 * never in the Redux store (the two calls it makes dispatch nothing at all), never in storage,
 * never in a URL, never rendered. The loop unwinds on teardown rather than being left suspended,
 * so nothing keeps the value alive past the panel.
 */
export const useQrSignIn = ({ enabled, onReady }: Params): QrSignInResult => {
  const [phase, setPhase] = useState<QrSignInPhase>('loading');
  const [url, setUrl] = useState<string | null>(null);
  // Bumped by `retry()` to restart the effect after a failed code request.
  const [restartKey, setRestartKey] = useState(0);

  const onReadyRef = useRef(onReady);
  onReadyRef.current = onReady;

  const retry = useCallback(() => setRestartKey(key => key + 1), []);

  useEffect(() => {
    if (!enabled) {
      // Drop the abandoned code, so reopening the panel can never flash a QR that is already dead.
      setPhase('loading');
      setUrl(null);
      return;
    }

    let cancelled = false;
    let timeout: ReturnType<typeof setTimeout>;
    let releaseWait: (() => void) | undefined;

    // Resolves early on teardown, so the loop below always reaches its `cancelled` check and
    // unwinds instead of being left suspended on a timer that will never fire.
    const wait = (ms: number) =>
      new Promise<void>(resolve => {
        releaseWait = resolve;
        timeout = setTimeout(resolve, ms);
      });

    const requestCode = async (): Promise<QrSignInCode | null> => {
      try {
        return await createQrSignInCode();
      } catch {
        return null;
      }
    };

    const readStatus = async (args: QrSignInPollArgs): Promise<QrSignInStatus | null> => {
      try {
        return await readQrSignInStatus(args);
      } catch {
        // A failed request is not a state change — keep waiting on the same code.
        return null;
      }
    };

    const run = async () => {
      while (!cancelled) {
        setPhase('loading');
        setUrl(null);

        const created = await requestCode();
        if (cancelled) return;

        if (!created?.code || !created?.poll_secret || !created?.url) {
          setPhase('error');
          return;
        }

        const { code, poll_secret: pollSecret, url: codeUrl } = created;
        const lifetimeMs = created.expires_in ? created.expires_in * 1000 : FALLBACK_LIFETIME_MS;
        const replaceAfterMs = Math.max(lifetimeMs - REPLACE_CODE_BEFORE_EXPIRY_MS, POLL_INTERVAL_MS);

        setUrl(codeUrl);
        setPhase('showing');

        const shownAt = Date.now();
        let replaceCode = false;

        while (!cancelled && !replaceCode) {
          await wait(POLL_INTERVAL_MS);
          if (cancelled) return;

          if (Date.now() - shownAt >= replaceAfterMs) {
            replaceCode = true;
            break;
          }

          const status = await readStatus({ code, poll_secret: pollSecret });
          if (cancelled) return;

          if (!status) continue;

          if (status.status === 'ready') {
            cancelled = true;
            onReadyRef.current({ user: status.user, token: status.token });
            return;
          }

          // `expired` covers a dead code, a wrong secret and an already-collected one. All three
          // recover the same way, and none of them is worth telling the dealer about.
          if (status.status === 'expired') {
            replaceCode = true;
          }
        }
      }
    };

    run();

    return () => {
      cancelled = true;
      clearTimeout(timeout);
      if (releaseWait) releaseWait();
    };
  }, [enabled, restartKey]);

  return { phase, url, retry };
};
